import { useState, useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import { Route, Switch } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import Home from "./pages/Home";
import Dashboard from "./pages/Dashboard";
import Login from "./pages/Login";
import SplashScreen from "./components/SplashScreen";
import MainLayout from "./components/MainLayout";

interface AuthUser {
  id: number;
  email: string;
  name: string;
  role: string;
}

/**
 * ProtectedRoute - Componente que protege rutas requiriendo autenticación
 * Si el usuario no está autenticado, redirige a /login
 */
function ProtectedRoute({ component: Component }: { component: React.ComponentType }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedUser = localStorage.getItem("auth_user");
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (error) {
        console.error("Error parsing user:", error);
        localStorage.removeItem("auth_user");
      }
    }
    setLoading(false);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-500 rounded-full animate-spin mb-4">
            <div className="w-10 h-10 bg-slate-900 rounded-full"></div>
          </div>
          <p className="text-slate-400">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    window.location.href = "/login";
    return null;
  }

  // Envolver el componente con MainLayout para que tenga Navbar
  return (
    <MainLayout>
      <Component />
    </MainLayout>
  );
}

/**
 * Router - Define todas las rutas de la aplicación
 * 
 * Estructura de rutas:
 * - /login - Página de login (pública)
 * - / - Home protegido
 * - /dashboard - Dashboard protegido
 * - /404 - Página no encontrada
 */
function Router() {
  return (
    <Switch>
      {/* Rutas públicas */}
      <Route path={"/login"} component={Login} />

      {/* Rutas protegidas */}
      <Route path={"/"}>{() => <ProtectedRoute component={Home} />}</Route>
      <Route path={"/dashboard"}>{() => <ProtectedRoute component={Dashboard} />}</Route>

      {/* Rutas de error */}
      <Route path={"/404"} component={NotFound} />
      <Route component={NotFound} />
    </Switch>
  );
}

/**
 * App - Componente raíz de la aplicación
 * Proporciona:
 * - Tema oscuro/claro
 * - Splash screen inicial
 * - Sistema de notificaciones (toast)
 * - Rutas
 */
function App() {
  const [showSplash, setShowSplash] = useState(true);

  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="dark">
        <TooltipProvider>
          <Toaster position="top-right" richColors closeButton />
          {showSplash && (
            <SplashScreen onComplete={() => setShowSplash(false)} />
          )}
          <Router />
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
